/**
 * Wishlist API Client
 * Handles all communication with the RESTful wishlist API
 * with automatic authentication via redirect flow
 */

class WishlistApiClient {
  constructor(options = {}) {
    this.baseUrl = options.baseUrl || '';
    this.pendingActionKey = 'wishlist_pending_action';
  }

  /**
   * Get API origin for auth redirect
   */
  getApiOrigin() {
    try {
      return new URL(this.baseUrl).origin;
    } catch {
      return this.baseUrl;
    }
  }

  /**
   * Core request method with authentication retry
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      method: options.method || 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${window.config.hydrogenBearer}`,
        ...options.headers,
      },
    };

    if (options.body) {
      config.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, config);

      // Handle 401 status
      if (response.status === 401) {
        return this.handleAuthError({ endpoint, options });
      }

      // Try to parse JSON response
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error(`Invalid JSON response from ${endpoint}`);
      }

      // Check for unauthorized error in response body
      if (data.error === 'Unauthorized') {
        return this.handleAuthError({ endpoint, options });
      }

      if (!response.ok) {
        throw new Error(data.message || data.error || `API error: ${response.status}`);
      }

      return data;
    } catch (error) {
      // Network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.warn('[WishlistAPI] Network error:', error.message);
        throw new Error('Network error - please check your connection');
      }
      throw error;
    }
  }

  /**
   * Handle authentication error by redirecting to login
   * Saves pending action to sessionStorage for retry after login
   */
  handleAuthError(failedRequest) {
    // NO guardar pending action si es bulk-add (ya está en sessionStorage como wishlist_pending_merge)
    if (!failedRequest.endpoint?.includes('bulk-add')) {
      const pendingAction = {
        endpoint: failedRequest.endpoint,
        options: failedRequest.options,
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.pendingActionKey, JSON.stringify(pendingAction));
    }

    // Redirect to login with current URL as origin
    const apiOrigin = this.getApiOrigin();
    const currentUrl = window.location.href;
    window.location.href = `${apiOrigin}/web-login?origin-url=${encodeURIComponent(currentUrl)}`;

    // Return a promise that never resolves (page will redirect)
    return new Promise(() => { });
  }

  /**
   * Check and execute any pending action after login redirect
   * Returns true if there was a pending action, false otherwise
   */
  async processPendingAction() {
    const stored = sessionStorage.getItem(this.pendingActionKey);
    if (!stored) return false;

    try {
      const pendingAction = JSON.parse(stored);

      // Clear the pending action immediately to prevent loops
      sessionStorage.removeItem(this.pendingActionKey);

      // Check if action is still valid (within 5 minutes)
      const fiveMinutes = 5 * 60 * 1000;
      if (Date.now() - pendingAction.timestamp > fiveMinutes) {
        console.warn('[WishlistAPI] Pending action expired');
        return false;
      }

      // Retry the request
      await this.request(pendingAction.endpoint, pendingAction.options);
      return true;
    } catch (error) {
      console.error('[WishlistAPI] Failed to process pending action:', error);
      sessionStorage.removeItem(this.pendingActionKey);
      return false;
    }
  }

  /**
   * Clear any pending action (useful when user logs out)
   */
  clearPendingAction() {
    sessionStorage.removeItem(this.pendingActionKey);
  }

  // ========================================
  // API Methods
  // ========================================

  /**
   * Get a specific wishlist or the default one
   * GET /rest/1/customers/wishlist/:id_wishlist?
   */
  async getWishlist(wishlistId = '0') {
    const endpoint = `/rest/1/customers/wishlist/${wishlistId}`;
    return this.request(endpoint);
  }

  /**
   * List all wishlists for the current user
   * GET /rest/1/customers/wishlist/actions/list
   */
  async listWishlists() {
    const endpoint = '/rest/1/customers/wishlist/actions/list';
    return this.request(endpoint);
  }

  /**
   * Add a product to a wishlist
   * POST /rest/1/customers/wishlist/add/:id_product/:id_product_attribute/:id_wishlist?reduced=true
   */
  async addProduct(productId, variantId, wishlistId = '0') {
    const endpoint = `/rest/1/customers/wishlist/add/${productId}/${variantId}/${wishlistId}?reduced=true`;
    return this.request(endpoint, { method: 'POST' });
  }

  /**
   * Remove a product from a wishlist
   * POST /rest/1/customers/wishlist/remove/:id_product/:id_product_attribute/:id_wishlist?reduced=true
   */
  async removeProduct(productId, variantId, wishlistId = '0') {
    const endpoint = `/rest/1/customers/wishlist/remove/${productId}/${variantId}/${wishlistId}?reduced=true`;
    return this.request(endpoint, { method: 'POST' });
  }

  /**
   * Add multiple products to a wishlist in one request
   * POST /rest/1/customers/wishlist/bulk-add
   */
  async bulkAdd(products, wishlistId = '0') {
    const endpoint = '/rest/1/customers/wishlist/bulk-add';
    return this.request(endpoint, {
      method: 'POST',
      body: {
        products: products.map(p => ({
          id_product: p.id_product || p.productId || p.id,
          id_product_attribute: p.id_product_attribute || p.variantId || ''
        })),
        id_wishlist: wishlistId
      }
    });
  }

  /**
   * Create a new wishlist
   * POST /rest/1/customers/wishlist/actions/add
   */
  async createWishlist(name) {
    const endpoint = '/rest/1/customers/wishlist/actions/add';
    return this.request(endpoint, {
      method: 'POST',
      body: { name },
    });
  }

  /**
   * Update a wishlist name
   * POST /rest/1/customers/wishlist/actions/update
   */
  async updateWishlist(wishlistId, name) {
    const endpoint = '/rest/1/customers/wishlist/actions/update';
    return this.request(endpoint, {
      method: 'POST',
      body: { id_wishlist: wishlistId, name },
    });
  }

  /**
   * Delete a wishlist
   * POST /rest/1/customers/wishlist/actions/delete
   */
  async deleteWishlist(wishlistId) {
    const endpoint = '/rest/1/customers/wishlist/actions/delete';
    return this.request(endpoint, {
      method: 'POST',
      body: { id_wishlist: wishlistId },
    });
  }
}

// Export for use in wishlist.js
window.WishlistApiClient = WishlistApiClient;
